import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import {
  Alert,
  Avatar,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setDialog } from "../features/dialog/dialogSlice";
import useAuth from "../hooks/useAuth";
import { selectCurrentUserData } from "../features/auth/authSlice";
import {
  useAddTicketCommentMutation,
  useCreateTicketMutation,
  useGetMyTicketsQuery,
  useGetSuperAssignableUsersQuery,
  useGetSuperTicketsQuery,
  useGetTicketByIdQuery,
  useUpdateSuperTicketMutation,
} from "../features/support/supportApiSlice";

const CATEGORY_OPTIONS = [
  { value: "TECHNICAL", label: "Technik" },
  { value: "BILLING", label: "Abrechnung" },
  { value: "ACCOUNT", label: "Konto" },
  { value: "OTHER", label: "Sonstiges" },
];

const PRIORITY_OPTIONS = [
  { value: "LOW", label: "Niedrig" },
  { value: "MEDIUM", label: "Mittel" },
  { value: "HIGH", label: "Hoch" },
  { value: "URGENT", label: "Dringend" },
];

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Offen", color: "info" },
  { value: "IN_PROGRESS", label: "In Bearbeitung", color: "warning" },
  { value: "WAITING_FOR_USER", label: "Warten auf Nutzer", color: "default" },
  { value: "RESOLVED", label: "Gelöst", color: "success" },
  { value: "CLOSED", label: "Geschlossen", color: "default" },
];

const formatUserLabel = (user) => {
  if (!user) return "Unbekannt";
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return fullName || user.mail || "Unbekannt";
};

function TicketStatusChip({ status }) {
  const config = STATUS_OPTIONS.find((entry) => entry.value === status);
  return <Chip size="small" label={config?.label || status} color={config?.color || "default"} />;
}

function TicketCard({ ticket, onOpen }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardActionArea onClick={() => onOpen(ticket)} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <TicketStatusChip status={ticket.status} />
              <Chip
                size="small"
                variant="outlined"
                label={PRIORITY_OPTIONS.find((entry) => entry.value === ticket.priority)?.label || ticket.priority}
              />
              <Chip
                size="small"
                variant="outlined"
                label={CATEGORY_OPTIONS.find((entry) => entry.value === ticket.category)?.label || ticket.category}
              />
            </Stack>
            <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}>
              {ticket.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
              {(ticket.description || "").slice(0, 180)}
              {(ticket.description || "").length > 180 ? "..." : ""}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary">
                Erstellt: {dayjs(ticket.createdAt).format("DD.MM.YYYY HH:mm")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Letzte Aktivität: {dayjs(ticket.updatedAt).format("DD.MM.YYYY HH:mm")}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function TicketDetailDialog({ open, onClose, ticketId, superView }) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUserData);
  const [message, setMessage] = useState("");
  const { data: ticket, isLoading, isError, refetch } = useGetTicketByIdQuery(
    { id: ticketId, superView },
    { skip: !open || !ticketId },
  );
  const [addComment, { isLoading: isSendingComment }] = useAddTicketCommentMutation();
  const [updateSuperTicket, { isLoading: isUpdatingTicket }] = useUpdateSuperTicketMutation();

  const tenantId = ticket?.tenantId?._id || ticket?.tenantId || "";
  const { data: assignableUsers = [] } = useGetSuperAssignableUsersQuery(
    { tenantId },
    { skip: !superView || !tenantId },
  );

  const handleAddComment = async () => {
    const normalized = String(message || "").trim();
    if (!normalized) return;

    try {
      await addComment({ id: ticketId, message: normalized, superView }).unwrap();
      setMessage("");
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: error?.data?.message || "Nachricht konnte nicht gesendet werden." },
        }),
      );
    }
  };

  const handleSuperFieldUpdate = async (field, value) => {
    if (!superView || !ticketId) return;

    try {
      await updateSuperTicket({ id: ticketId, [field]: value }).unwrap();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: error?.data?.message || "Ticket konnte nicht aktualisiert werden." },
        }),
      );
    }
  };

  const sortedComments = useMemo(() => {
    const comments = Array.isArray(ticket?.comments) ? [...ticket.comments] : [];
    return comments.sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());
  }, [ticket?.comments]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Ticket-Details</Typography>
          <Typography variant="body2" color="text.secondary">
            Verlauf, Status und Kommunikation im Überblick.
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <Typography variant="body2">Laden...</Typography>}
        {isError && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="error">
              Ticket konnte nicht geladen werden.
            </Typography>
            <Button size="small" onClick={refetch}>
              Erneut laden
            </Button>
          </Stack>
        )}
        {!isLoading && ticket && (
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.light, 0.08),
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
              }}
            >
              <Stack spacing={1}>
                <Typography variant="h6">{ticket.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                  {ticket.description}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <TicketStatusChip status={ticket.status} />
                  <Chip
                    size="small"
                    label={PRIORITY_OPTIONS.find((entry) => entry.value === ticket.priority)?.label || ticket.priority}
                  />
                  <Chip
                    size="small"
                    label={CATEGORY_OPTIONS.find((entry) => entry.value === ticket.category)?.label || ticket.category}
                    variant="outlined"
                  />
                  {ticket.tenantId?.tenantName && (
                    <Chip size="small" variant="outlined" label={`Mandant: ${ticket.tenantId.tenantName}`} />
                  )}
                </Stack>
              </Stack>
            </Paper>

            {superView && (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={ticket.status}
                    onChange={(event) => handleSuperFieldUpdate("status", event.target.value)}
                    disabled={isUpdatingTicket}
                    fullWidth
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Priorität"
                    value={ticket.priority}
                    onChange={(event) => handleSuperFieldUpdate("priority", event.target.value)}
                    disabled={isUpdatingTicket}
                    fullWidth
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Zuweisung"
                    value={ticket.assignedTo?._id || ""}
                    onChange={(event) =>
                      handleSuperFieldUpdate("assignedTo", event.target.value || null)
                    }
                    disabled={isUpdatingTicket}
                    fullWidth
                  >
                    <MenuItem value="">Nicht zugewiesen</MenuItem>
                    {assignableUsers.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {formatUserLabel(user)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Divider />
              </>
            )}

            <Stack spacing={1.25}>
              <Typography variant="subtitle1">Konversation</Typography>
              {sortedComments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Nachrichten.
                </Typography>
              )}
              {sortedComments.map((comment) => {
                const isOwn = comment?.createdBy?._id === currentUser?.id;
                return (
                  <Stack
                    key={comment._id}
                    direction="row"
                    spacing={1}
                    justifyContent={isOwn ? "flex-end" : "flex-start"}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: "92%",
                        p: 1.25,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: (theme) =>
                          isOwn ? alpha(theme.palette.primary.main, 0.4) : "divider",
                        bgcolor: (theme) =>
                          isOwn
                            ? alpha(theme.palette.primary.light, 0.18)
                            : alpha(theme.palette.grey[100], 0.8),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Avatar sx={{ width: 22, height: 22, fontSize: "0.7rem" }}>
                          {(comment?.createdBy?.firstName?.[0] || "").toUpperCase()}
                          {(comment?.createdBy?.lastName?.[0] || "").toUpperCase()}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          {formatUserLabel(comment?.createdBy)} -{" "}
                          {dayjs(comment.createdAt).format("DD.MM.YYYY HH:mm")}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {comment.message}
                      </Typography>
                    </Paper>
                  </Stack>
                );
              })}
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Neue Nachricht"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleAddComment}
                disabled={isSendingComment}
                sx={{ minWidth: { xs: "100%", sm: 130 }, minHeight: 56 }}
              >
                Senden
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SupportPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    category: "TECHNICAL",
    priority: "MEDIUM",
  });

  const isOverviewRoute = location.pathname.endsWith("/support/overview");
  const currentTab = isSuperAdmin && isOverviewRoute ? "overview" : "my";

  const { data: myTickets = [], isLoading: loadingMyTickets, isError: myTicketsError } =
    useGetMyTicketsQuery();
  const {
    data: allTickets = [],
    isLoading: loadingAllTickets,
    isError: allTicketsError,
  } = useGetSuperTicketsQuery(undefined, { skip: !isSuperAdmin });
  const [createTicket, { isLoading: creatingTicket }] = useCreateTicketMutation();

  const ticketsForList = currentTab === "overview" ? allTickets : myTickets;
  const isListLoading = currentTab === "overview" ? loadingAllTickets : loadingMyTickets;
  const isListError = currentTab === "overview" ? allTicketsError : myTicketsError;

  const stats = useMemo(() => {
    const source = currentTab === "overview" ? allTickets : myTickets;
    return {
      total: source.length,
      open: source.filter((ticket) => ticket.status === "OPEN").length,
      inProgress: source.filter((ticket) => ticket.status === "IN_PROGRESS").length,
      resolved: source.filter((ticket) =>
        ["RESOLVED", "CLOSED"].includes(ticket.status),
      ).length,
    };
  }, [allTickets, currentTab, myTickets]);

  const handleTabChange = (_event, newValue) => {
    if (newValue === "overview") {
      navigate("/dashboard/support/overview");
      return;
    }
    navigate("/dashboard/support");
  };

  const handleCreateTicket = async () => {
    const payload = {
      title: String(newTicket.title || "").trim(),
      description: String(newTicket.description || "").trim(),
      category: newTicket.category,
      priority: newTicket.priority,
    };

    if (!payload.title || !payload.description) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehlende Angaben",
          data: { errorText: "Bitte Titel und Beschreibung ausfüllen." },
        }),
      );
      return;
    }

    try {
      await createTicket(payload).unwrap();
      setNewTicket({
        title: "",
        description: "",
        category: "TECHNICAL",
        priority: "MEDIUM",
      });
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Ticket erstellt",
          data: { successText: "Ihr Ticket wurde erfolgreich erstellt." },
        }),
      );
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: error?.data?.message || "Ticket konnte nicht erstellt werden." },
        }),
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Support | Mitgliederverwaltung</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        <Card
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: 2.5,
            bgcolor: (theme) => alpha(theme.palette.info.light, 0.08),
            borderColor: (theme) => alpha(theme.palette.info.main, 0.35),
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={1.5}>
              <Typography variant="h4">Support</Typography>
              <Typography variant="body2" color="text.secondary">
                Fragen, technische Probleme oder Abrechnungsthemen zentral im Ticket-System.
              </Typography>

              {isSuperAdmin && (
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  allowScrollButtonsMobile
                >
                  <Tab value="my" label={`Meine Tickets (${myTickets.length})`} />
                  <Tab value="overview" label={`Ticket-Übersicht (${allTickets.length})`} />
                </Tabs>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Gesamt: ${stats.total}`} />
                <Chip color="info" label={`Offen: ${stats.open}`} />
                <Chip color="warning" label={`In Bearbeitung: ${stats.inProgress}`} />
                <Chip color="success" label={`Gelöst/Geschlossen: ${stats.resolved}`} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={2}>
          {currentTab === "my" && (
            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="h6">Neues Ticket erstellen</Typography>
                <TextField
                  label="Titel"
                  value={newTicket.title}
                  onChange={(event) =>
                    setNewTicket((prev) => ({ ...prev, title: event.target.value }))
                  }
                  fullWidth
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    select
                    label="Kategorie"
                    value={newTicket.category}
                    onChange={(event) =>
                      setNewTicket((prev) => ({ ...prev, category: event.target.value }))
                    }
                    fullWidth
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Priorität"
                    value={newTicket.priority}
                    onChange={(event) =>
                      setNewTicket((prev) => ({ ...prev, priority: event.target.value }))
                    }
                    fullWidth
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  label="Beschreibung"
                  value={newTicket.description}
                  onChange={(event) =>
                    setNewTicket((prev) => ({ ...prev, description: event.target.value }))
                  }
                  multiline
                  minRows={4}
                  fullWidth
                />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCreateTicket}
                  disabled={creatingTicket}
                  sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
                >
                  Ticket erstellen
                </Button>
              </Stack>
            </Paper>
          )}

          {currentTab === "overview" && isSuperAdmin && (
            <Alert severity="info">
              SuperAdmin-Ansicht: Hier sehen Sie alle Tickets mandantenübergreifend und können
              Status, Priorität und Zuweisung steuern.
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
            <Stack spacing={1.25}>
              <Typography variant="h6">
                {currentTab === "overview" ? "Ticket-Übersicht" : "Meine Tickets"}
              </Typography>
              {isListLoading && (
                <Typography variant="body2" color="text.secondary">
                  Lade Tickets...
                </Typography>
              )}
              {isListError && (
                <Typography variant="body2" color="error">
                  Tickets konnten nicht geladen werden.
                </Typography>
              )}
              {!isListLoading && !isListError && ticketsForList.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Tickets vorhanden.
                </Typography>
              )}
              <Stack spacing={1.25}>
                {ticketsForList.map((ticket) => (
                  <TicketCard
                    key={ticket._id}
                    ticket={ticket}
                    onOpen={() => setSelectedTicketId(ticket._id)}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Container>

      <TicketDetailDialog
        open={Boolean(selectedTicketId)}
        onClose={() => setSelectedTicketId(null)}
        ticketId={selectedTicketId}
        superView={currentTab === "overview" && isSuperAdmin}
      />
    </>
  );
}
